'use client';

import { StatusBadge } from '@/components/common';
import { type MaturityModel } from '@/domain/maturityModel/types';
import { useMaturityFilterStore } from '@/features/maturityModel/store/maturityFilter';
import { type GroupBy } from '@/features/maturityModel/types';
import { getHeader, getStatusColor } from '@/features/maturityModel/utils';
import { Dropdown } from 'primereact/dropdown';
import { Suspense, useMemo } from 'react';

import MaturityHistory from './MaturityHistory';
import styles from './MaturitySection.module.scss';
import MaturitySectionTable from './MaturitySectionTable';

interface MaturitySectionProps {
  maturity: MaturityModel;
  idProduct: number;
}

const getTotalMaturity = (maturity: MaturityModel, groupBy: GroupBy) => {
  const items = maturity.groups.flatMap((g) => g.items.filter((i) => i.selectedOption?.maturityLevel !== -1));
  return groupBy === 'Grouping by Average'
    ? Math.round(items.reduce((a, i) => a + (i.selectedOption?.maturityLevel ?? 0), 0) / items.length)
    : Math.min(...items.map((i) => i.selectedOption?.maturityLevel ?? 0));
};

const MaturitySection = ({ maturity, idProduct }: MaturitySectionProps) => {
  const groupBy = useMaturityFilterStore((s) => s.groupBy);

  const total = useMemo(() => (maturity ? getTotalMaturity(maturity, groupBy) : 0), [groupBy, maturity]);

  if (!maturity)
    return (
      <div className={styles.container}>
        <div className={styles.section}>
          <h4>Maturity</h4>
          This product doesn&apos;t have a maturity defined!
        </div>
      </div>
    );

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <h4 style={{ marginRight: '0.5rem' }}>
          Maturity{' '}
          <StatusBadge fontSize={15} color={getStatusColor(total)}>
            {getHeader(total)}
          </StatusBadge>
        </h4>
        <Dropdown
          value={groupBy}
          options={['Grouping by Minimum', 'Grouping by Average']}
          onChange={(e) => {
            setTimeout(() => {
              useMaturityFilterStore.setState({ groupBy: e.value });
            }, 0);
          }}
          style={{ width: '15rem' }}
        />
      </div>
      <Suspense>
        <MaturityHistory idProduct={idProduct} />
      </Suspense>
      <MaturitySectionTable maturity={maturity} />
    </div>
  );
};

export default MaturitySection;
